import { useState } from 'react';
import { CalendarEvent } from '../types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { fromLocalISOString } from '@/utils/timezoneUtils';

export const useManualSwap = () => {
  const [markedShiftForSwap, setMarkedShiftForSwap] = useState<CalendarEvent | null>(null);

  const markShiftForSwap = (event: CalendarEvent) => {
    setMarkedShiftForSwap(event);
    toast.info('Shift marked for swap', {
      description: `${event.title} - ${fromLocalISOString(event.start_date).toLocaleString()}`,
    });
  };

  const clearMarkedShift = () => {
    setMarkedShiftForSwap(null);
    toast.info('Swap cancelled');
  };

  const swapShifts = async (targetEvent: CalendarEvent, onSuccess?: () => void) => {
    if (!markedShiftForSwap) {
      toast.error('No shift marked for swap');
      return;
    }

    try {
      // Fetch current shift data to get assigned_guards arrays
      const { data: shift1Data, error: shift1Error } = await supabase
        .from('team_schedules')
        .select('assigned_guards')
        .eq('id', markedShiftForSwap.id)
        .single();

      const { data: shift2Data, error: shift2Error } = await supabase
        .from('team_schedules')
        .select('assigned_guards')
        .eq('id', targetEvent.id)
        .single();

      if (shift1Error || shift2Error || !shift1Data || !shift2Data) {
        throw new Error('Failed to fetch shift data');
      }

      // Swap the assigned_guards arrays
      const { error: update1Error } = await supabase
        .from('team_schedules')
        .update({ assigned_guards: shift2Data.assigned_guards })
        .eq('id', markedShiftForSwap.id);

      if (update1Error) throw update1Error;

      const { error: update2Error } = await supabase
        .from('team_schedules')
        .update({ assigned_guards: shift1Data.assigned_guards })
        .eq('id', targetEvent.id);

      if (update2Error) throw update2Error;

      toast.success('Shifts swapped successfully', {
        description: `Swapped guards between "${markedShiftForSwap.title}" and "${targetEvent.title}"`,
      });

      setMarkedShiftForSwap(null);
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error('Error swapping shifts:', error);
      toast.error('Failed to swap shifts', {
        description: error.message,
      });
    }
  };

  return {
    markedShiftForSwap,
    markShiftForSwap,
    clearMarkedShift,
    swapShifts,
  };
};
